import { setItemToLocalStorage } from '../utils/setLocalStorage'
import axios from '../src/axios'
import { ENDPOINTS } from './constants'
const getPrevValueFromStorage = (key: string) => {
  if (typeof window === 'undefined') return null
  let value = window.localStorage.getItem(key)
  if (value === 'null') value = null
  return value
}
const initialState = {
  authAttempted: false,
  // True once SessionCheck has settled the session question (signed in, signed out, or
  // undeterminable). UI that would otherwise flash a logged-out state while a silent
  // refresh is in flight should wait on this. Unlike `authAttempted` it is also set for
  // logged-out visitors.
  authResolved: false,
  userId: getPrevValueFromStorage('user_id'),
  username: getPrevValueFromStorage('username'),
  auth: null,
  userProfile: {},
  settings: {},
  shoppingUserProfile: {},
  instagramInfo: {},
  afterAddShoppingItem: false,
  cjUserPidsByUserId: {} as Record<number, string>,
  cjPidsBySegment: {} as Record<string, string>,
  cjUserPidsLoadState: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
  suspendedBrandIds: [] as number[],
  blockedNetworks: [] as string[],
  suspendedBrandsLoadState: 'idle' as
    | 'idle'
    | 'loading'
    | 'succeeded'
    | 'failed',
}

const appStateReducer = (state: any, action: any) => {
  switch (action.type) {
    case 'AUTH_CHANGE': {
      setItemToLocalStorage('user_id', action.userId)
      setItemToLocalStorage('username', action.userProfile.userName)

      return {
        ...state,
        auth: action.auth,
        authAttempted: true,
        authResolved: true,
        userId: action.userId,
        username: action.userProfile.userName,
        userProfile: action.userProfile,
      }
    }
    case 'SIGN_IN': {
      const userId = action.auth.attributes['custom:user_id']
      setItemToLocalStorage('user_id', userId)
      setItemToLocalStorage('username', action.userProfile.userName)
      window.localStorage.setItem('loggedInRefresh', Date.now().toString())
      // axios.get(ENDPOINTS.increaseVisitsByOne.replace(':id', userId))
      return {
        ...state,
        auth: action.auth,
        authAttempted: true,
        authResolved: true,
        userId: userId,
        username: action.userProfile.userName,
        userProfile: action.userProfile,
      }
    }
    // The session is definitively gone (no stored user, or Cognito rejected the refresh
    // token). Clears local hints but, unlike SIGN_OUT, does not broadcast `logoutAllTabs`,
    // so other tabs are not forced to reload.
    case 'AUTH_RESOLVED_SIGNED_OUT': {
      window.localStorage.removeItem('user_id')
      window.localStorage.removeItem('username')
      window.localStorage.removeItem('profile')
      return {
        ...state,
        auth: null,
        authResolved: true,
        userId: null,
        username: null,
        userProfile: {},
      }
    }
    // The session could not be determined (network/5xx). Keep the cached optimistic state
    // rather than guessing "signed out"; a later request or reload will retry.
    case 'AUTH_RESOLVED_UNKNOWN': {
      return { ...state, authResolved: true }
    }
    case 'UPDATE_USER_DATA': {
      return {
        ...state,
        username: action.userProfile.userName,
        userProfile: action.userProfile,
      }
    }
    case 'UPDATE_SHOPPING_USER_DATA': {
      return {
        ...state,
        shoppingUserProfile: action.shoppingUserProfile,
      }
    }
    case 'UPDATE_AFTER_ADDING_TO_SHOPPING_BOARD': {
      return {
        ...state,
        afterAddShoppingItem: action.afterAddShoppingItem,
      }
    }
    case 'UPDATE_INSTAGRAM_INFOMATION': {
      return {
        ...state,
        instagramInfo: action.instagramInfo,
      }
    }
    case 'SIGN_OUT': {
      window.localStorage.removeItem('user_id')
      window.localStorage.removeItem('username')
      window.localStorage.removeItem('profile')
      window.localStorage.removeItem('personalInfoTrigger')
      window.localStorage.setItem('logoutAllTabs', Date.now().toString())
      sessionStorage.removeItem('activeOnSite')
      return {
        ...initialState,
        authResolved: true,
        userId: null,
        username: null,
      }
    }
    case 'UPDATE_SETTINGS': {
      return {
        ...state,
        settings: action.settings,
      }
    }
    case 'SET_CJ_USER_PIDS_LOADING': {
      return {
        ...state,
        cjUserPidsLoadState: 'loading',
        cjUserPidsByUserId: {},
        cjPidsBySegment: {},
      }
    }
    case 'SET_CJ_USER_PIDS': {
      return {
        ...state,
        cjUserPidsLoadState: 'succeeded',
        cjUserPidsByUserId: action.cjUserPidsByUserId || {},
        cjPidsBySegment: action.cjPidsBySegment || {},
      }
    }
    case 'SET_CJ_USER_PIDS_ERROR': {
      return {
        ...state,
        cjUserPidsLoadState: 'failed',
        cjUserPidsByUserId: {},
        cjPidsBySegment: {},
      }
    }
    case 'RESET_CJ_USER_PIDS': {
      return {
        ...state,
        cjUserPidsLoadState: 'idle',
        cjUserPidsByUserId: {},
        cjPidsBySegment: {},
      }
    }
    case 'SET_SUSPENDED_BRANDS_LOADING': {
      return {
        ...state,
        suspendedBrandsLoadState: 'loading',
        suspendedBrandIds: [],
        blockedNetworks: [],
      }
    }
    case 'SET_SUSPENDED_BRANDS_SUCCESS': {
      return {
        ...state,
        suspendedBrandsLoadState: 'succeeded',
        suspendedBrandIds: action.suspendedBrandIds || [],
        blockedNetworks: action.blockedNetworks || [],
      }
    }
    case 'SET_SUSPENDED_BRANDS_ERROR': {
      return {
        ...state,
        suspendedBrandsLoadState: 'failed',
        suspendedBrandIds: [],
        blockedNetworks: [],
      }
    }
    case 'RESET_SUSPENDED_BRANDS': {
      return {
        ...state,
        suspendedBrandsLoadState: 'idle',
        suspendedBrandIds: [],
        blockedNetworks: [],
      }
    }
    default:
      throw new Error('Unknown action type')
  }
}

export { initialState }
export default appStateReducer
